import React, { useState } from 'react';
import { PaperPlaneRight, CheckCircle, SpinnerGap, WhatsappLogo } from '@phosphor-icons/react';
import { publicApi } from '../services/api';
import { getServiceColors } from './ServiceContent';

const WHATSAPP = '917350695792';

const enquiryWhatsAppHref = (enquiry) => {
  if (!enquiry) return `https://wa.me/${WHATSAPP}`;
  const lines = [
    'Hi Growmore IT Services,',
    '',
    'I just sent an enquiry on the website. Please follow up.',
    '',
    `Name: ${enquiry.name || '—'}`,
    `Phone: ${enquiry.phone || '—'}`,
    `Email: ${enquiry.email || '—'}`
  ];
  if (enquiry.company) lines.push(`Company: ${enquiry.company}`);
  if (enquiry.service) lines.push(`Regarding: ${enquiry.service}`);
  lines.push('', `Message: ${enquiry.message || '—'}`);
  return `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(lines.join('\n'))}`;
};

const ServiceEnquiryForm = ({ service }) => {
  const colors = getServiceColors(service.color);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [lastEnquiry, setLastEnquiry] = useState(null);
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    company: '',
    message: `I am interested in ${service.title}. Please share more details.`
  });

  const onChange = (event) => {
    setForm((prev) => ({ ...prev, [event.target.name]: event.target.value }));
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    setStatus('loading');
    setError('');

    try {
      await publicApi.post('/enquiries', {
        ...form,
        service_slug: service.slug,
        service_name: service.title,
        source_page: window.location.pathname,
        source_label: `${service.title} page`,
        source_type: 'service_page'
      });
      setLastEnquiry({ ...form, service: service.title });
      setStatus('success');
      setForm({
        name: '',
        phone: '',
        email: '',
        company: '',
        message: `I am interested in ${service.title}. Please share more details.`
      });
    } catch (err) {
      setStatus('idle');
      setError(err.response?.data?.message || 'Could not send enquiry. Please try again or call us.');
    }
  };

  if (status === 'success') {
    return (
      <div className={`rounded-2xl border ${colors.border} ${colors.bg} p-6 text-center`}>
        <CheckCircle className={`w-10 h-10 mx-auto mb-3 ${colors.text}`} weight="fill" />
        <h3 className="text-display font-bold text-gray-900 dark:text-white mb-2">Enquiry sent</h3>
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-5">
          Thank you. Our team will contact you about {service.title} shortly.
        </p>
        <a
          href={enquiryWhatsAppHref(lastEnquiry)}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm"
        >
          <WhatsappLogo className="w-5 h-5" weight="fill" />
          Send this enquiry on WhatsApp
        </a>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="rounded-2xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-dark-800 p-4 sm:p-6 shadow-soft">
      <p className={`text-xs font-bold uppercase tracking-wider mb-1 ${colors.text}`}>
        {service.title} enquiry
      </p>
      <h3 className="text-display text-lg sm:text-xl font-extrabold text-gray-900 dark:text-white mb-4">
        Request a callback
      </h3>

      <div className="space-y-2.5">
        <input
          name="name"
          required
          value={form.name}
          onChange={onChange}
          placeholder="Your name"
          className="input-field !py-2.5"
        />
        <input
          name="phone"
          required
          value={form.phone}
          onChange={onChange}
          placeholder="Phone number"
          className="input-field !py-2.5"
        />
        <input
          name="email"
          type="email"
          required
          value={form.email}
          onChange={onChange}
          placeholder="Email"
          className="input-field !py-2.5"
        />
        <input
          name="company"
          value={form.company}
          onChange={onChange}
          placeholder="Company name (optional)"
          className="input-field !py-2.5"
        />
        <textarea
          name="message"
          required
          rows={3}
          value={form.message}
          onChange={onChange}
          className="input-field resize-none !py-2.5"
        />
      </div>

      {error && <p className="text-sm text-red-500 mt-3">{error}</p>}

      <button
        type="submit"
        disabled={status === 'loading'}
        className="btn-primary text-white w-full mt-4 inline-flex items-center justify-center gap-2 disabled:opacity-70"
      >
        {status === 'loading' ? (
          <SpinnerGap className="w-5 h-5 animate-spin" />
        ) : (
          <PaperPlaneRight className="w-5 h-5" weight="bold" />
        )}
        Send {service.title} enquiry
      </button>
    </form>
  );
};

export default ServiceEnquiryForm;
